import { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, User, Phone, Mail, Building2, CheckCircle2 } from 'lucide-react';
// AddressAutocomplete import removed - address now collected in Corporation Info step
import { formatPhoneNumber } from '@/utils/formatters';

interface PersonalInformationStepProps {
  onSubmit: (data: { personalInfo: any; applicationType: 'individual' | 'corporation' }) => void;
  initialData?: any;
  shouldShowField?: (fieldPath: string) => boolean;
  isFieldRequired?: (fieldPath: string) => boolean;
  getFieldDefaultValue?: (fieldPath: string, includeWhenVisible?: boolean) => string | undefined;
  sectionTitle?: string;
  sectionSubtitle?: string;
}

export function PersonalInformationStep({ 
  onSubmit, 
  initialData,
  shouldShowField = () => true,
  isFieldRequired = () => true,
  getFieldDefaultValue,
  sectionTitle = "Tell us about yourself",
  sectionSubtitle = "Let's start with some basic information about you. This will help us personalize your application experience."
}: PersonalInformationStepProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create dynamic schema based on conditional requirements
  const personalInfoSchema = useMemo(() => {
    const baseSchema: any = {};

    // Only add fields to schema if they should be shown
    if (shouldShowField('personalInfo.firstName')) {
      baseSchema.firstName = isFieldRequired('personalInfo.firstName')
        ? z.string().min(1, 'First name is required').max(50, 'First name must be less than 50 characters')
        : z.string().max(50, 'First name must be less than 50 characters').optional();
    }

    if (shouldShowField('personalInfo.lastName')) {
      baseSchema.lastName = isFieldRequired('personalInfo.lastName')
        ? z.string().min(1, 'Last name is required').max(50, 'Last name must be less than 50 characters')
        : z.string().max(50, 'Last name must be less than 50 characters').optional();
    }

    if (shouldShowField('personalInfo.email')) {
      baseSchema.email = isFieldRequired('personalInfo.email')
        ? z.string().email('Please enter a valid email address')
        : z.string().email('Please enter a valid email address').optional();
    }

    if (shouldShowField('personalInfo.phone')) {
      baseSchema.phone = isFieldRequired('personalInfo.phone')
        ? z.string().min(10, 'Phone number must be at least 10 digits').regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Please enter a valid phone number')
        : z.string().regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Please enter a valid phone number').optional();
    }

    // Personal address removed - now collected in Corporation Info step

    // Application type is always required
    baseSchema.applicationType = z.enum(['individual', 'corporation'], {
      required_error: 'Please select an application type',
    });

    return z.object(baseSchema);
  }, [shouldShowField, isFieldRequired]);

  type PersonalInfoData = z.infer<typeof personalInfoSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<PersonalInfoData>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      firstName: initialData?.personalInfo?.firstName || getFieldDefaultValue?.('personalInfo.firstName') || '',
      lastName: initialData?.personalInfo?.lastName || getFieldDefaultValue?.('personalInfo.lastName') || '',
      email: initialData?.personalInfo?.email || getFieldDefaultValue?.('personalInfo.email') || '',
      phone: initialData?.personalInfo?.phone || getFieldDefaultValue?.('personalInfo.phone') || '',
      // personalAddress removed - now collected in Corporation Info step
      applicationType: initialData?.applicationType || undefined,
    },
    mode: 'onBlur',
  });

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const cleanedValue = rawValue.replace(/\D/g, '');
    
    // Limit to 10 digits
    const truncatedValue = cleanedValue.slice(0, 10);
    
    // Format the phone number
    let formattedValue = '';
    if (truncatedValue.length > 0) {
      if (truncatedValue.length <= 3) {
        formattedValue = truncatedValue;
      } else if (truncatedValue.length <= 6) {
        formattedValue = `(${truncatedValue.slice(0, 3)}) ${truncatedValue.slice(3)}`;
      } else {
        formattedValue = `(${truncatedValue.slice(0, 3)}) ${truncatedValue.slice(3, 6)}-${truncatedValue.slice(6)}`;
      }
    }
    
    setValue('phone', formattedValue, {
      shouldValidate: false,
    });
  };

  // handleAddressChange removed - address now collected in Corporation Info step

  const onFormSubmit = async (data: PersonalInfoData) => {
    try {
      setIsSubmitting(true);
      setServerError(null);
      
      console.log('PersonalInformationStep form data:', data);
      
      const { applicationType, ...personalInfo } = data;

      // Ensure defaults for hidden fields are applied when not shown
      const fields = ['firstName', 'lastName', 'email', 'phone'] as const;
      const submissionPersonalInfo: any = { ...personalInfo };
      fields.forEach((field) => {
        const fieldPath = `personalInfo.${field}`;
        if (!shouldShowField(fieldPath)) {
          const defVal = getFieldDefaultValue?.(fieldPath);
          if (defVal !== undefined) {
            submissionPersonalInfo[field] = defVal;
          }
        }
      });

      const submitData = { personalInfo: submissionPersonalInfo, applicationType };
      
      console.log('PersonalInformationStep submitting:', submitData);
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('PersonalInformationStep submission error:', error);
      setServerError(`An error occurred while saving your information: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          {sectionTitle}
        </CardTitle>
        <CardDescription>
          {sectionSubtitle}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {serverError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        )}

        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shouldShowField('personalInfo.firstName') && (
            <div className="space-y-2">
              <Label htmlFor="firstName">
                First Name {isFieldRequired('personalInfo.firstName') && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="firstName"
                {...register('firstName')}
                placeholder="John"
                autoComplete="given-name"
              />
              {errors.firstName && (
                <p className="text-sm text-red-600">{String(errors.firstName.message)}</p>
              )}
            </div>
          )}

          {shouldShowField('personalInfo.lastName') && (
            <div className="space-y-2">
              <Label htmlFor="lastName">
                Last Name {isFieldRequired('personalInfo.lastName') && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="lastName"
                {...register('lastName')}
                placeholder="Doe"
                autoComplete="family-name"
              />
              {errors.lastName && (
                <p className="text-sm text-red-600">{String(errors.lastName.message)}</p>
              )}
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shouldShowField('personalInfo.email') && (
            <div className="space-y-2">
              <Label htmlFor="email">
                Email Address {isFieldRequired('personalInfo.email') && <span className="text-red-500">*</span>}
              </Label>
              <div className="relative">
                <Input
                  id="email"
                  {...register('email')}
                  type="email"
                  placeholder="john@example.com"
                  autoComplete="email"
                  className="pl-10"
                />
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600">{String(errors.email.message)}</p>
              )}
            </div>
          )}

          {shouldShowField('personalInfo.phone') && (
            <div className="space-y-2">
              <Label htmlFor="phone">
                Phone Number {isFieldRequired('personalInfo.phone') && <span className="text-red-500">*</span>}
              </Label>
              <div className="relative">
                <Input
                  id="phone"
                  onChange={handlePhoneChange}
                  value={watch('phone')}
                  placeholder="(XXX) XXX-XXXX"
                  autoComplete="tel"
                  className="pl-10"
                />
                <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              {errors.phone && (
                <p className="text-sm text-red-600">{String(errors.phone.message)}</p>
              )}
            </div>
          )}
        </div>

        {/* Address Information removed - now collected in Corporation Info step */}

        {/* Application Type */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Are you applying as an individual or business?</h3>
          
          <Controller
            name="applicationType"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    field.value === 'individual' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => field.onChange('individual')}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <div 
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={field.value === 'individual'}
                          onCheckedChange={() => field.onChange('individual')}
                        />
                      </div>
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">Individual</h4>
                      <p className="text-sm text-gray-600">Personal application</p>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    field.value === 'corporation' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => field.onChange('corporation')}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <div 
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={field.value === 'corporation'}
                          onCheckedChange={() => field.onChange('corporation')}
                        />
                      </div>
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">Business / Corporation</h4>
                      <p className="text-sm text-gray-600">Business entity application</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          />

          {errors.applicationType && (
            <p className="text-sm text-red-600">{String(errors.applicationType.message)}</p>
          )}

          {/* Information Box */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">What's the difference?</h4>
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                <strong>Individual:</strong> Choose this if you're a sole proprietor, freelancer, or applying personally. You'll need your driver's license, SSN, and date of birth.
              </p>
              <p>
                <strong>Business / Corporation:</strong> Choose this if you have a registered business entity (LLC, Corporation, Partnership, etc.). You'll need your EIN, business details, and corporate information.
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end pt-6">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </CardContent>
    </form>
  );
}
